/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@duxton-web/ui"
import { WarningFilled } from "@duxton-web/icons"
import { datadogRum } from "@datadog/browser-rum"
import { useTranslationsMap, resolveCopy } from "common/utils/i18n"
import { ddLogEvent } from "utils/datadog"
import { sleep } from "utils/misc"
import { subscribeToSessionError } from "utils/sessionError"
import { refreshNativeAuthSession } from "utils/nativeBridge"
import {
  sendWebviewSessionErrorEvent,
  sendWebviewSessionRetryEvent,
  SessionRetryOutcome,
} from "utils/webviewTelemetry"

import styles from "./SessionErrorPage.modules.css"

/**
 * How long to wait for the app to settle `refreshAuthSession` before reloading
 * anyway. A handler the app never resolves must not leave Retry doing nothing,
 * since it is the merchant's only exit.
 *
 * Generous on purpose: the app re-runs GrabID before it answers, so this is a
 * network round trip, not a local call. The predecessor `reopen` waited 1500ms
 * because the app was expected to destroy the webview and make the wait moot —
 * keeping that budget here would reload with the STALE cookie mid-refresh and
 * land the merchant straight back on this screen.
 */
const REFRESH_TIMEOUT_MS = 10000
const RETRY_DELAY_MS = 500

/**
 * TEMPORARY — English fallbacks for the session-error copy.
 *
 * The three PhraseApp keys are mapped in
 * `scripts/phraseapp/phraseapp_config.json`, but they carry no source copy yet,
 * so the generated `public/locales/*` either publish them as "" or omit them
 * altogether. Without a fallback this screen would render a blank title, blank
 * body and an unlabelled Retry button: the merchant's only exit from a dead
 * session, with nothing on it.
 *
 * TODO: delete this object and read `strings.*` directly once the copy is
 * published and pulled — `resolveCopy` hands over to the translation
 * automatically the moment it is real. Confirm with
 * `yarn check-missing-locales:fresh --strict`.
 */
export const SESSION_ERROR_TEXT_FALLBACK = {
  title: "Something went wrong",
  description: "We couldn't load this page. Please try again.",
  retry: "Retry",
}

/**
 * Full-screen, retryable error shown when the session dies inside the app
 * webview (HTTP 401). The webview has no login flow of its own, so the merchant
 * is offered a retry rather than a portal login page they cannot use. Retry
 * asks the app to renew the session in place (`refreshAuthSession`) — the app
 * owns the session, so only it can — and then reloads the page, which is the
 * half the app deliberately leaves to the web side. See
 * `http401UnauthorizedHandler`.
 */
export const SessionErrorPage: FC = () => {
  const [hasSessionError, setHasSessionError] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  // The watchdog and a late bridge answer can both end the retry —
  // clearTimeout cannot cancel a timer that has already fired, so the guard,
  // not the cancellation, is what makes the reload (and its report) single-shot.
  const retrySettled = useRef(false)

  useEffect(
    () =>
      subscribeToSessionError(reason => {
        setHasSessionError(true)
        // The merchant's session is dead and Retry is their only exit; without
        // this signal there is no way to see how often the webview loses a
        // session. `reason` separates a rejected token from an incomplete
        // handover by the app — the screen is the same, the owner is not.
        //
        // Reported to both sinks deliberately: Datadog is where this alerts,
        // Scribe is where it can be read as a rate against
        // WEBVIEW_SESSION_START and sliced by app version and platform.
        datadogRum.addError("Webview session error", {
          tags: { context: "SessionErrorPage", reason },
        })
        ddLogEvent("session_error", { reason }, "error")
        sendWebviewSessionErrorEvent(reason)
      }),
    [],
  )

  /**
   * Ends the retry: reports how it went, then reloads.
   *
   * The reload is unconditional, and that is the design. A successful refresh
   * needs one to pick up the new cookies; a failed one has nowhere better to go
   * — the reload re-raises this same screen, which is where the merchant
   * already was, and nothing here can renew a session the app could not. What
   * separates the two is the report, not the behaviour. There is no automatic
   * second attempt, so an unrecoverable session cannot loop: each reload costs
   * a deliberate press.
   */
  const settleRetry = useCallback(
    async (outcome: SessionRetryOutcome, error?: string) => {
      if (retrySettled.current) return
      retrySettled.current = true
      /*
       * `finally`, because the latch is already set: if reporting threw, the
       * caller's catch would re-enter here, return on the latch, and leave the
       * merchant on a dead screen with a spinner. Retry is their only exit, so
       * instrumentation must not be able to suppress the reload.
       *
       * The await is what gets the event out at all — it is emitted one line
       * before a navigation that would otherwise cancel it — and it is capped
       * inside `sendWebviewSessionRetryEvent`, so a dead Scribe delays the exit
       * by a bounded amount rather than blocking it.
       */
      try {
        datadogRum.addError("Webview session error retry", {
          tags: {
            context: "SessionErrorPage",
            outcome,
            ...(error ? { error } : {}),
          },
        })
        // No `error`: the bridge's message is free text the redaction rules
        // cannot vouch for.
        ddLogEvent("session_refresh", { outcome })
        await sendWebviewSessionRetryEvent(outcome, error)
      } catch {
        // Nothing to recover: the reload below is the point of this function.
      } finally {
        window.location.reload()
      }
    },
    [],
  )

  // Re-entry is blocked by `loading` on the Button (Duxton swallows clicks
  // while loading), which doubles as feedback during the refresh wait.
  const handleRetry = useCallback(async () => {
    setIsRetrying(true)
    // Let loading render so Retry does not appear stuck.
    await sleep(RETRY_DELAY_MS)
    // Armed before the call, not after it: an app that takes the call and never
    // answers leaves nothing else to fire.
    const watchdog = window.setTimeout(() => {
      // Nothing awaits this one: `settleRetry` swallows its own failures and
      // ends in the reload either way.
      settleRetry("timeout")
    }, REFRESH_TIMEOUT_MS)
    try {
      const refresh = await refreshNativeAuthSession()
      window.clearTimeout(watchdog)
      // No bridge (desktop browser, or the ?webview=true override): reloading
      // is the only lever the page has, and nothing was asked of the app.
      if (refresh === null) await settleRetry("noBridge")
      else if (!refresh.ok) await settleRetry("failed", refresh.error)
      // Resolved, but never said the refresh worked — what an app build that
      // does not know this handler answers. Reloads exactly like a success;
      // reported apart from one, or a partial app rollout is invisible.
      else if (!refresh.confirmed) await settleRetry("noAnswer")
      else await settleRetry("success")
    } catch (error) {
      window.clearTimeout(watchdog)
      await settleRetry(
        "rejected",
        error instanceof Error ? error.message : String(error),
      )
    }
  }, [settleRetry])

  const strings = useTranslationsMap({
    title: "common.sessionError.title",
    description: "common.sessionError.description",
    retry: "common.sessionError.retry",
  })

  if (!hasSessionError) return null

  // Composed from primitives instead of Duxton `Result`: nothing else in the
  // app imports `Result`, and it statically bundles three ~15KB illustration
  // SVGs this screen never shows — using it here would push all of that into
  // the main chunk for a component that almost always renders null.
  return (
    <div className={styles.overlay} role="alert">
      <div className={styles.content}>
        <WarningFilled className={styles.icon} />
        {/* TEMPORARY: see SESSION_ERROR_TEXT_FALLBACK above. */}
        <div className={styles.title}>
          {resolveCopy(strings.title, SESSION_ERROR_TEXT_FALLBACK.title)}
        </div>
        <div className={styles.description}>
          {resolveCopy(
            strings.description,
            SESSION_ERROR_TEXT_FALLBACK.description,
          )}
        </div>
        <Button
          type="primary"
          className={styles.retryButton}
          loading={isRetrying}
          onClick={handleRetry}
        >
          {resolveCopy(strings.retry, SESSION_ERROR_TEXT_FALLBACK.retry)}
        </Button>
      </div>
    </div>
  )
}
